"use client";

import { cn } from "@aichat/ui/lib/utils";
import {
  File,
  FileText,
  ImageIcon,
  Loader2,
  TriangleAlert,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../tooltip";

export type FileUploadState = {
  file: File;
  uploadedUrl?: string;
  status: "loading" | "error" | "success" | "idle";
  errorMessage?: string;
};

function AttachmentPreview(props: {
  state: FileUploadState;
  className?: string;
  onRemove: () => void;
}) {
  const { onRemove, state, className } = props;
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  const isImage = state.file.type.startsWith("image/");
  const isDocument =
    state.file.type.includes("pdf") ||
    state.file.type.includes("document") ||
    state.file.type.includes("text");

  // Create thumbnail for images
  useEffect(() => {
    if (isImage) {
      const url = URL.createObjectURL(state.file);
      setThumbnailUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [state.file, isImage]);

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  // Get file icon based on type
  const getFileIcon = () => {
    if (isImage) return ImageIcon;
    if (isDocument) return FileText;
    return File;
  };

  const FileIcon = getFileIcon();

  return (
    <div className={cn("space-y-1", className)}>
      <div
        className={cn(
          "rounded-lg h-[60px] flex items-center gap-3 p-3 bg-neutral-50 border relative",
          state.status === "error" && "border-destructive bg-destructive/5",
          state.status === "loading" && "bg-neutral-100",
        )}
      >
        {/* File preview/icon */}
        <div className="flex-shrink-0 w-9 h-9 rounded overflow-hidden bg-neutral-200 flex items-center justify-center">
          {state.status === "loading" ? (
            <Loader2 className="size-4 animate-spin" />
          ) : isImage &&
            thumbnailUrl &&
            (state.status === "success" ||
              state.status === "idle" ||
              state.status === "error") ? (
            <img
              src={thumbnailUrl || "/placeholder.svg"}
              alt={state.file.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <FileIcon className="size-4 text-neutral-600" />
          )}
        </div>

        {/* File info */}
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <div
            className="truncate text-sm font-medium text-neutral-900 max-w-[100px]"
            title={state.file.name}
          >
            {state.file.name}
          </div>
          <div className="text-xs text-neutral-500">
            {formatFileSize(state.file.size)}
          </div>
        </div>

        {/* Status and actions */}
        <div className="flex-shrink-0 flex items-center gap-1">
          {state.status === "error" && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <TriangleAlert className="size-4 text-destructive" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{state.errorMessage || "Upload failed"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <button
            type="button"
            onClick={onRemove}
            className="p-1 hover:bg-neutral-200 rounded-sm transition-colors"
          >
            <Trash2 className="size-4 text-neutral-500 hover:text-neutral-700" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default AttachmentPreview;
